// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
/**
 * Pure query helpers for every CRM list table — sort toggling, request-param
 * shaping and URL facet (de)serialisation — extracted so they can be unit
 * tested without mounting a component or standing up axios.
 *
 * Generalises the since-retired `customer-invoices-query.ts`, which proved this
 * shape on the customer Billing tab (that tab now runs on THIS module — see
 * `customer-invoices-query.test.ts`). The crm-api list endpoints sort on
 * `sort` + `dir` and
 * only honour ALLOW-LISTED columns; sending a column the server does not
 * declare is harmless — the server keeps its default order.
 */

export type SortDir = "asc" | "desc";

export interface SortState {
  /** Server column key. Must match the controller's `sortable_columns`. */
  sort: string;
  dir: SortDir;
}

/**
 * Facet key → selected values. An empty array means "not filtering on this".
 *
 * Parameterised on the page's facet-key UNION (not left as a bare
 * `Record<string, …>`) so a typo'd key is a COMPILE error at the page rather
 * than a param the server quietly ignores. `K` flows from the page config's
 * `facetKeys` through `useTableQuery` into `buildTableParams`, whose result
 * the page assigns to its own typed filter interface — which is where a key
 * the API does not implement finally fails to type-check.
 */
export type FacetValues<K extends string = string> = Record<K, string[]>;

/**
 * The request params for one page of a list, with the page's own facet keys
 * spelled out. `page`/`per_page` always travel; a facet only appears when
 * something is selected, hence the `?`.
 *
 * `sort`/`dir` are optional for ONE reason, and it is not "the caller might
 * forget them": `buildTableParams` deliberately OMITS them while a search is
 * running and the viewer has not picked a column — see that function. Every
 * other call emits both.
 */
export type TableParams<K extends string = never> = {
  page: number;
  per_page: number;
  sort?: string;
  dir?: SortDir;
  search?: string;
} & { [P in K]?: string };

/**
 * Next sort state when a header is clicked. Clicking the ALREADY-active
 * column flips direction; clicking a different column selects it ascending.
 * Mirrors the MUI `<TableSortLabel>` convention.
 */
export function nextSortState(current: SortState, column: string): SortState {
  if (current.sort === column) {
    return { sort: column, dir: current.dir === "asc" ? "desc" : "asc" };
  }
  return { sort: column, dir: "asc" };
}

/**
 * Build the request params for one page of a CRM list table. Emits the
 * canonical `sort` + `dir` pair — NEVER the legacy `sort_by` /
 * `sort_direction` names, which the API silently ignores.
 *
 * Multi-select facets travel as one comma-separated param (the Rails side
 * splits on commas into an IN clause); an empty facet is omitted entirely
 * rather than sent as an empty string, and a blank search is omitted rather
 * than sent as `search=` (which would still match everything but pollutes
 * the request/cache key).
 *
 * SEARCH RELEVANCE — why `sortExplicit` exists.
 *
 * Three crm-api list actions (invoices/quotes, contacts, tasks) rank a
 * SEARCHED list by relevance instead of by a column: an exact hit on the
 * identity column outranks a prefix hit, which outranks a bare substring, plus
 * flat bonuses for a row that matched only through a child record (e.g.
 * `Invoice.by_search_relevance` adds +50 for a customer-contact hit and +60
 * for a deal-title hit). Each of those actions guards that branch with
 * `unless sort_requested?` — i.e. it ranks by relevance only when the request
 * NAMED no declared sort column.
 *
 * `useTableQuery` resolves `sort` to the page's DEFAULT when the URL carries
 * none, so a page that always forwarded `{ sort, dir }` here sent `sort=` on
 * every request — `sort_requested?` was then permanently true and the
 * relevance branch became unreachable from the product. Searching silently
 * fell back to creation order on all three pages. (Found 2026-08-28 while
 * converting Invoices + Quotes; Contacts had regressed the same way one task
 * earlier.)
 *
 * So the rule lives HERE, once, rather than in each page's params memo: while
 * a non-blank search is active AND the viewer has not explicitly chosen a
 * column (`sortExplicit === false`, from `useTableQuery`), both `sort` and
 * `dir` are omitted and the server ranks. An explicit sort always WINS over
 * relevance, which is what the server-side tests pin.
 *
 * `sortExplicit` defaults to TRUE so every existing caller — and every list
 * whose API has no relevance branch at all — keeps sending the pair exactly as
 * before. Only a page that passes `table.sortExplicit` opts into the rule.
 */
export function buildTableParams<K extends string>(
  sort: SortState,
  facets: FacetValues<K>,
  page: number,
  perPage: number,
  search?: string,
  sortExplicit: boolean = true,
): TableParams<K> {
  const trimmedSearch = search?.trim();
  // Omit the KEYS entirely rather than setting them to `undefined`: the params
  // object is spread into a page's filter interface and handed to axios, and a
  // present-but-undefined `sort` is a different thing from an absent one to
  // both `'sort' in params` checks and to `exactOptionalPropertyTypes`.
  const rankBySearchRelevance = Boolean(trimmedSearch) && !sortExplicit;
  const params: Record<string, string | number> = {
    page,
    per_page: perPage,
    ...(rankBySearchRelevance ? {} : { sort: sort.sort, dir: sort.dir }),
  };
  for (const [key, values] of Object.entries<string[]>(facets)) {
    if (values.length > 0) params[key] = values.join(",");
  }
  if (trimmedSearch) params.search = trimmedSearch;
  // The ONE assertion in this contract, and it lives here rather than at each
  // of the nine call sites: the loop above builds the object key-by-key, which
  // TypeScript cannot track back to the mapped type. Everything a caller
  // touches is `TableParams<K>`, so a page still gets its facet keys checked
  // against its own filter interface.
  return params as TableParams<K>;
}

/** Serialise facets into a flat string map suitable for `URLSearchParams`. */
export function serializeFacets<K extends string>(facets: FacetValues<K>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, values] of Object.entries<string[]>(facets)) {
    if (values.length > 0) out[key] = values.join(",");
  }
  return out;
}

/**
 * Parse facets back out of `URLSearchParams`, restricted to the declared
 * `keys` — any other param on the URL (however it got there) is ignored, so
 * an unrecognised query string can never smuggle in an undeclared facet.
 */
export function parseFacets<K extends string>(
  sp: URLSearchParams,
  keys: readonly K[],
): FacetValues<K> {
  const out = {} as FacetValues<K>;
  for (const key of keys) {
    const raw = sp.get(key);
    out[key] = raw ? raw.split(",").filter(Boolean) : [];
  }
  return out;
}

/** Count of selected facet values across all facets, not the number of facet keys. */
export function countActiveFacets<K extends string>(facets: FacetValues<K>): number {
  return Object.values<string[]>(facets).reduce((n, values) => n + values.length, 0);
}
